import React, { Component } from 'react';
import { HiMicrophone, HiVideoCamera, HiPhone, HiDesktopComputer } from 'react-icons/hi';
import { BsRecordCircle, BsStopCircle } from 'react-icons/bs';

import Video from '../../components/call/video'
import Global from '../../globals/globals'
import MediaSoupClient from '../../services/mediasoupClient'
import RecordingService from '../../services/recordingService'

class App extends Component {
  constructor(props) {
    super(props)

    this.state = {
      localStream: null,
      remoteStream: null,
      remoteStreams: [], // Array of { id, name, stream, audio?, video? }
      selectedVideo: null,
      status: 'Please wait...',
      isMicMuted: false,
      isCameraOff: false,
      buttonAnimations: {},
      isMobile: false,
      // New MediaSoup-specific state
      isRecording: false,
      recordingSupported: RecordingService.isSupported(),
      isScreenSharing: false,
      localScreenStream: null, // Our own screen share stream for preview
      remoteScreenShare: null, // { peerId, stream }
      screenShareSupported: false,
      connectionState: 'connecting',
      peerLeft: false,
      countdown: 5,
      focusedStreamId: null
    }

    this.room = props.room || window.location.pathname
    this.serviceIP = Global.serviceIP
    this.mediasoupServerUrl = this.convertServiceIP(props.mediasoupServerUrl || Global.serviceIP)
    this.onHangup = props.onHangup
    this.callType = props.callType || 'audio'
    this.peerName = props.peerName || 'Unknown'
    this.localName = props.localName || 'You'

    // MediaSoup client and recording service refs
    this.mediasoupClient = null
    this.recordingService = null
    this.countdownInterval = null
    
    // Remote streams map for MediaSoup (peerId -> { audio, video })
    this.remoteStreamsMap = new Map()
  }

  
  convertServiceIP = (serviceIP) => {
    if (!serviceIP || typeof serviceIP !== 'string') return Global.serviceIP
    let url = serviceIP.trim()
    url = url.replace(/\/webrtcPeer\s*["']?\s*$/i, '').trim()
    url = url.replace(/["'\s]+$/, '').trim()
    const doubleProtocolMatch = url.match(/^https?:\/\/(https?:\/\/.+)$/i)
    if (doubleProtocolMatch) url = doubleProtocolMatch[1]
    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const parsed = new URL(url)
        const isLocalhost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1'
        const protocol = isLocalhost ? 'http:' : parsed.protocol
        url = `${protocol}//${parsed.host}`
      } catch (e) {
        return Global.serviceIP
      }
    } else {
      if (!url || url === 'http' || url === 'https') return Global.serviceIP
      const isLocalhost = url.startsWith('localhost') || url.startsWith('127.0.0.1')
      url = (isLocalhost ? 'http://' : 'https://') + url
    }
    console.log('[Call] Converted MediaSoup URL:', url)
    return url
  }
  /**
   * Initialize MediaSoup connection and start producing media
   */
  initializeMediaSoup = async () => {
    try {
      // Create MediaSoup client with the SFU server URL
      this.mediasoupClient = new MediaSoupClient(this.mediasoupServerUrl)

      // Set up event callbacks
      this.mediasoupClient.onRemoteStream = this.handleRemoteStream
      this.mediasoupClient.onRemoteStreamRemoved = this.handleRemoteStreamRemoved
      this.mediasoupClient.onPeerJoined = this.handlePeerJoined
      this.mediasoupClient.onPeerLeft = this.handlePeerLeft
      this.mediasoupClient.onScreenShare = this.handleScreenShare
      this.mediasoupClient.onScreenShareStopped = this.handleScreenShareStopped
      this.mediasoupClient.onConnectionStateChange = this.handleConnectionStateChange

      // Connect to signaling server
      await this.mediasoupClient.connect()

      // Get local media stream
      const hasVideo = this.callType === 'audio+video'
      const stream = await this.mediasoupClient.getLocalStream(true, hasVideo)
      this.setState({ localStream: stream })

      // Join room (using room ID from props)
      await this.mediasoupClient.joinRoom(this.room)

      // Create send and receive transports
      await this.mediasoupClient.createTransports()

      // Consume any existing producers in the room
      await this.mediasoupClient.consumePendingProducers()

      await this.mediasoupClient.produce(stream)

      this.setState({
        status: 'Waiting for other peers to connect',
        connectionState: 'connected'
      })

      console.log('[Call] MediaSoup initialized successfully')
    } catch (err) {
      console.error('[Call] Failed to initialize MediaSoup:', err)
      this.setState({
        status: 'Connection failed',
        connectionState: 'failed'
      })
    }
  }

  /**
   * Handle incoming remote stream from a peer
   */
  handleRemoteStream = (peerId, kind, stream) => {
    console.log(`[Call] handleRemoteStream called: peerId=${peerId}, kind=${kind}, stream.active=${stream?.active}, tracks=${stream?.getTracks().map(t => `${t.kind}:${t.readyState}:enabled=${t.enabled}:muted=${t.muted}`).join(', ')}`)
    // Update the streams map
    const existing = this.remoteStreamsMap.get(peerId) || {}
    this.remoteStreamsMap.set(peerId, { ...existing, [kind]: stream })

    // Update state for UI
    this.setState(prev => {
      // Check if we already have this peer
      let remoteStreams = [...prev.remoteStreams]
      const existingIndex = remoteStreams.findIndex(s => s.id === peerId)

      if (existingIndex >= 0) {
        const existingEntry = remoteStreams[existingIndex]
        const combinedStream = new MediaStream()
        
        // Keep existing tracks that aren't of the same kind as the new one
        if (existingEntry.stream) {
          existingEntry.stream.getTracks().forEach(track => {
            if (track.kind !== kind) {
              combinedStream.addTrack(track)
            }
          })
        }
        // Add the new track(s)
        stream.getTracks().forEach(track => {
          combinedStream.addTrack(track)
        })

        const updated = {
          ...existingEntry,
          stream: combinedStream,
          audio: kind === 'audio' ? stream : existingEntry.audio,
          video: kind === 'video' ? stream : existingEntry.video
        }
        remoteStreams[existingIndex] = updated
      } else {
        const newEntry = {
          id: peerId,
          name: this.peerName,
          stream: stream, // Use the incoming stream (either audio or video)
          audio: kind === 'audio' ? stream : null,
          video: kind === 'video' ? stream : null
        }
        remoteStreams = [...remoteStreams, newEntry]
      }

      // Update selected video - use the updated entry if it matches
      let selectedVideo = prev.selectedVideo
      const updatedEntry = remoteStreams.find(s => s.id === peerId)
      if (!selectedVideo) {
        selectedVideo = updatedEntry
      } else if (selectedVideo.id === peerId) {
        selectedVideo = updatedEntry
      }

      // Get combined audio stream for audio-only calls (still needed for audio-only mode)
      const peersWithAudio = remoteStreams.filter(s => s.audio)
      const remoteStream = peersWithAudio.length > 0 ? peersWithAudio[0].audio : prev.remoteStream

      return {
        remoteStreams,
        selectedVideo,
        remoteStream,
        status: `${this.peerName}`
      }
    }, () => {
      if (this.recordingService && this.state.isRecording) {
        this.recordingService.updateRemoteStreams(this.remoteStreamsMap)
        this.updateRecordingLayout()
      }
    })
  }

  /**
   * Handle removal of a remote stream
   */
  handleRemoteStreamRemoved = (peerId, kind) => {
    const existing = this.remoteStreamsMap.get(peerId)
    if (existing) {
      delete existing[kind]
      if (Object.keys(existing).length === 0) {
        this.remoteStreamsMap.delete(peerId)
      } else {
        this.remoteStreamsMap.set(peerId, existing)
      }
    }

    this.setState(prev => {
      let remoteStreams = [...prev.remoteStreams]
      const existingIndex = remoteStreams.findIndex(s => s.id === peerId)

      if (existingIndex >= 0) {
        const updated = { ...remoteStreams[existingIndex] }
        if (kind === 'video') {
          updated.stream = null
          updated.video = null
        } else {
          updated.audio = null
        }

        // Remove if no streams left
        if (!updated.audio && !updated.video) {
          remoteStreams = remoteStreams.filter(s => s.id !== peerId)
        } else {
          remoteStreams[existingIndex] = updated
        }
      }

      return {
        remoteStreams,
        selectedVideo: remoteStreams.find(s => s.video) || null,
        status: remoteStreams.length > 0 ? `${this.peerName}` : 'Waiting for other peers to connect'
      }
    }, () => {
      if (this.recordingService && this.state.isRecording) {
        this.recordingService.updateRemoteStreams(this.remoteStreamsMap)
        this.updateRecordingLayout()
      }
    })
  }

  /**
   * Handle peer joining
   */
  handlePeerJoined = (peerId) => {
    console.log(`[Call] Peer joined: ${peerId}`)
    // Cancel any peer-left countdown if a peer (re)joins
    if (this.state.peerLeft) {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
      this.setState({
        peerLeft: false,
        countdown: 5,
        status: this.peerName || 'Connected'
      })
    }
  }

  /**
   * Handle peer leaving — starts auto-hangup countdown (Widget pattern)
   */
  handlePeerLeft = (peerId) => {
    console.log(`[Call] Peer left: ${peerId}`)
    this.remoteStreamsMap.delete(peerId)

    this.setState(prev => {
      const remoteStreams = prev.remoteStreams.filter(s => s.id !== peerId)
      return {
        remoteStreams,
        selectedVideo: remoteStreams.find(s => s.video) || null,
        remoteStream: remoteStreams.find(s => s.audio)?.audio || null,
        focusedStreamId: prev.focusedStreamId === peerId ? null : prev.focusedStreamId,
        status: remoteStreams.length > 0 ? `${this.peerName}` : 'Waiting for other peers to connect'
      }
    }, () => {
      // Only start hangup countdown when the last peer leaves
      if (this.state.remoteStreams.length === 0 && !this.state.peerLeft) {
        this.startPeerLeftCountdown()
      }
    })
  }

  startPeerLeftCountdown = () => {
    if (this.state.peerLeft) return  

    this.setState({
      peerLeft: true,
      countdown: 5,
      status: 'Peer left the call; waiting for them to rejoin (5s)'
    })

    this.countdownInterval = setInterval(() => {
      this.setState(prev => {
        if (prev.countdown > 1) {
          const next = prev.countdown - 1;
          return {
            countdown: next,
            status: `Peer left the call; waiting for them to rejoin (${next}s)`,
            peerLeft: prev.peerLeft
          }
        } else {
          if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
            this.countdownInterval = null
          }
          // Auto-hangup since peer did not rejoin
          this.cleanupCall()
          if (this.onHangup) this.onHangup()
          return { peerLeft: false, countdown: 5, status: 'Call ended' }
        }
      })
    }, 1000)
  }

  /**
   * Handle incoming screen share
   */
  handleScreenShare = (peerId, stream) => {
    console.log(`[Call] Received screen share from ${peerId}`)
    this.setState({ remoteScreenShare: { peerId, stream } }, () => {
      this.updateRecordingLayout()
    })
  }

  /**
   * Handle screen share stopped
   */
  handleScreenShareStopped = (peerId) => {
    console.log(`[Call] Screen share stopped from ${peerId}`)
    this.setState(prev => {
      if (!peerId || prev.remoteScreenShare?.peerId === peerId) {
        return { remoteScreenShare: null }
      }
      return null
    }, () => {
      this.updateRecordingLayout()
    })
  }

  /**
   * Handle connection state changes — triggers peer-left countdown on failure 
   */
  handleConnectionStateChange = (state) => {
    console.log(`[Call] Connection state: ${state}`)
    if (state === 'failed') {
      // Unrecoverable — start hangup countdown
      this.setState({
        connectionState: state,
        status: 'Connection lost'
      })
      if (!this.state.peerLeft) {
        this.startPeerLeftCountdown()
      }
    } else if (state === 'disconnected') {
      this.setState({
        connectionState: state,
        status: 'Reconnecting...'
      })
    } else if (state === 'connected') {
      // Cancel peer-left countdown if connection recovers
      if (this.state.peerLeft) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval)
          this.countdownInterval = null
        }
        this.setState({
          peerLeft: false,
          countdown: 5,
          connectionState: 'connected',
          status: this.peerName || 'Connected'
        })
      } else {
        this.setState({ connectionState: 'connected' })
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
    this.cleanupCall()
  }

  checkMobile = () => {
    this.setState({ isMobile: window.innerWidth < 768 });
  }

  /**
   * Cleanup call resources - callable from parent via ref
   */
  cleanupCall = async () => {
    // Stop recording if active
    if (this.recordingService && this.state.isRecording) {
      try {
        const blob = await this.recordingService.stopRecording()
        if (blob) {
          this.recordingService.downloadRecording(blob, `call-${this.room}-${Date.now()}.webm`)
        }
      } catch (err) {
        console.error('[Call] Failed to stop recording:', err)
      }
      this.recordingService = null
    }

    // Stop local stream
    if (this.state.localStream) {
      this.state.localStream.getTracks().forEach(track => track.stop())
    }

    // Disconnect MediaSoup client
    if (this.mediasoupClient) {
      this.mediasoupClient.disconnect()
      this.mediasoupClient = null
    }

    // Clear remote streams map
    this.remoteStreamsMap.clear()

    this.setState({
      localStream: null,
      remoteStream: null,
      remoteStreams: [],
      selectedVideo: null,
      status: 'Call ended',
      isMicMuted: false,
      isCameraOff: false,
      isRecording: false,
      isScreenSharing: false,
      remoteScreenShare: null,
      connectionState: 'disconnected'
    })
  }

  componentDidMount() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);

    // Feature-detect screen sharing support
    const screenShareSupported = !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getDisplayMedia === 'function'
    );

    this.setState({ screenShareSupported });

    // Initialize MediaSoup connection
    this.initializeMediaSoup();
  }

  switchVideo = video => {
    this.setState({ selectedVideo: video }, () => {
      this.updateRecordingLayout()
    })
  }

  /**
   * Toggle microphone mute using MediaSoup producer
   */
  toggleMic = () => {
    if (this.mediasoupClient) {
      const muted = this.mediasoupClient.toggleAudio()
      this.setState({ isMicMuted: muted })
    } else if (this.state.localStream) {
      // Fallback to track-level control
      const audioTracks = this.state.localStream.getAudioTracks()
      audioTracks.forEach(track => { track.enabled = this.state.isMicMuted })
      this.setState({ isMicMuted: !this.state.isMicMuted })
    }
  }

  /**
   * Toggle camera using MediaSoup producer
   */
  toggleCamera = () => {
    if (this.mediasoupClient) {
      const off = this.mediasoupClient.toggleVideo()
      this.setState({ isCameraOff: off })
    } else if (this.state.localStream) {
      // Fallback to track-level control
      const videoTracks = this.state.localStream.getVideoTracks()
      videoTracks.forEach(track => { track.enabled = this.state.isCameraOff })
      this.setState({ isCameraOff: !this.state.isCameraOff })
    }
  }

  /**
   * Toggle screen sharing
   */
  toggleScreenShare = async () => {
    if (!this.mediasoupClient) return

    if (!this.state.screenShareSupported) {
      alert('Screen sharing is not supported on this device/browser.');
      return;
    }

    try {
      if (this.state.isScreenSharing) {
        await this.mediasoupClient.stopScreenShare()
        this.setState({ isScreenSharing: false, localScreenStream: null }, () => {
          this.updateRecordingLayout()
        })
      } else {
        const screenStream = await this.mediasoupClient.startScreenShare()
        this.setState({ isScreenSharing: true, localScreenStream: screenStream }, () => {
          this.updateRecordingLayout()
        })
      }
    } catch (err) {
      console.error('[Call] Screen share error:', err)
      if (err.name !== 'NotAllowedError') {
        // User didn't cancel, show error
        console.error('[Call] Failed to share screen:', err.message)
      }
      this.setState({ isScreenSharing: false, localScreenStream: null })
    }
  }

  /**
   * Start recording the call
   */
  updateRecordingLayout = () => {
    if (!this.recordingService || !this.state.isRecording) return

    const mainStream = this.state.isScreenSharing
      ? (this.state.localScreenStream || this.state.remoteScreenShare?.stream)
      : this.state.selectedVideo?.stream || null

    this.recordingService.updateLayout({
      selectedVideoStream: mainStream,
      isScreenSharing: this.state.isScreenSharing,
      localScreenStream: this.state.localScreenStream,
      remoteScreenShare: this.state.remoteScreenShare
    })
  }

  startRecording = async () => {
    if (!this.state.recordingSupported || this.state.isRecording) return

    try {
      this.recordingService = new RecordingService()
      const mainStream = this.state.isScreenSharing
        ? (this.state.localScreenStream || this.state.remoteScreenShare?.stream)
        : this.state.selectedVideo?.stream || null

      await this.recordingService.startRecording(
        this.state.localStream,
        this.remoteStreamsMap,
        {
          selectedVideoStream: mainStream,
          isScreenSharing: this.state.isScreenSharing,
          localScreenStream: this.state.localScreenStream,
          remoteScreenShare: this.state.remoteScreenShare
        }
      )
      this.setState({ isRecording: true }, () => {
        this.updateRecordingLayout()
      })
    } catch (err) {
      console.error('[Call] Failed to start recording:', err)
      this.recordingService = null
    }
  }

  /**
   * Stop recording and download
   */
  stopRecording = async () => {
    if (!this.recordingService || !this.state.isRecording) return

    try {
      const blob = await this.recordingService.stopRecording()
      if (blob) {
        this.recordingService.downloadRecording(blob, `call-${this.room}-${Date.now()}.webm`)
      }
      this.recordingService = null
      this.setState({ isRecording: false })
    } catch (err) {
      console.error('[Call] Failed to stop recording:', err)
      this.setState({ isRecording: false })
    }
  }

  handleButtonClick = (action) => {
    this.setState(prevState => ({
      buttonAnimations: { ...prevState.buttonAnimations, [action]: true }
    }))

    setTimeout(() => {
      this.setState(prevState => ({
        buttonAnimations: { ...prevState.buttonAnimations, [action]: false }
      }))
    }, 150)
  }

  render() {
    const isVideoCall = this.callType === 'audio+video';
    const isMobile = this.state.isMobile;

    // Responsive sizes
    const pipSize = isMobile 
      ? { width: '120px', height: '90px', top: '12px', right: '12px' }
      : { width: '200px', height: '150px', top: '20px', right: '20px' };
    
    const buttonSize = isMobile ? '44px' : '48px';
    const hangupButtonSize = isMobile ? '52px' : '56px';
    const controlsGap = isMobile ? '12px' : '20px';
    const controlsBottom = isMobile ? '20px' : '30px';

    // Detect if any screen sharing is active
    const hasActiveScreenShare = this.state.isScreenSharing || this.state.remoteScreenShare;
    const thumbnailSize = isMobile ? { width: '100px', height: '75px' } : { width: '160px', height: '120px' };

    return (
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: isVideoCall 
          ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
          : 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        
        {/* Recording indicator */}
        {this.state.isRecording && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(239, 68, 68, 0.9)',
            color: '#fff',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            zIndex: 20,
            animation: 'pulse 2s infinite'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#fff',
              animation: 'pulse 1s infinite'
            }}></div>
            Recording
          </div>
        )}

        <div style={{
          flex: 1,
          position: 'relative',
          borderRadius: '0 0 12px 12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {isVideoCall ? (
            <>
              {/* Main Content Area - Screen share or selected video */}
              <div style={{
                flex: 1,
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Screen Share Display (Google Meet style) */}
                {hasActiveScreenShare ? (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    background: '#000',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{
                      padding: '8px 16px',
                      background: 'rgba(0,0,0,0.8)',
                      color: '#fff',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <HiDesktopComputer style={{ fontSize: '14px' }} />
                      {this.state.isScreenSharing ? 'You are presenting' : 'Screen shared by participant'}
                    </div>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <Video
                        videoStream={this.state.isScreenSharing ? this.state.localScreenStream : this.state.remoteScreenShare?.stream}
                        autoPlay
                        muted={this.state.isScreenSharing}
                        videoStyles={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          background: '#000'
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  /* Normal view - focused participant fills background */
                  <Video
                    videoStream={(() => {
                      if (this.state.remoteStreams.length > 1) {
                        const focusedId = this.state.focusedStreamId || this.state.remoteStreams[0]?.id;
                        const focused = this.state.remoteStreams.find(s => s.id === focusedId);
                        return focused?.video?.stream || focused?.stream || null;
                      }
                      return this.state.selectedVideo?.stream || null;
                    })()}
                    autoPlay
                    videoStyles={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)'
                    }}
                  />
                )}

                {/* Participant Strip (when screen sharing) - Google Meet style */}
                {hasActiveScreenShare && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100px',
                    right: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    zIndex: 15
                  }}>
                    {/* Local video thumbnail */}
                    <div style={{
                      width: thumbnailSize.width,
                      height: thumbnailSize.height,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                      border: '2px solid rgba(255,255,255,0.3)',
                      position: 'relative'
                    }}>
                      <Video
                        videoStream={this.state.localStream}
                        autoPlay
                        muted
                        videoStyles={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transform: 'scaleX(-1)'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: '4px',
                        left: '4px',
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '500'
                      }}>
                        You
                      </div>
                    </div>

                    {/* Remote video thumbnails */}
                    {this.state.remoteStreams.filter(s => s.type !== 'screen').map((stream, index) => (
                      <div key={stream.id || index} style={{
                        width: thumbnailSize.width,
                        height: thumbnailSize.height,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                        border: '2px solid rgba(255,255,255,0.3)',
                        position: 'relative'
                      }}>
                        <Video
                          videoStream={stream.stream}
                          autoPlay
                          videoStyles={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          bottom: '4px',
                          left: '4px',
                          background: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '500'
                        }}>
                          {stream.name || `Peer ${index + 1}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!hasActiveScreenShare && (
                  <div style={{
                    position: 'absolute',
                    top: pipSize.top,
                    right: pipSize.right,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    zIndex: 10
                  }}>
                    {/* Local "You" */}
                    <div style={{
                      width: pipSize.width,
                      height: pipSize.height,
                      borderRadius: isMobile ? '8px' : '12px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                      border: isMobile ? '2px solid rgba(255,255,255,0.2)' : '3px solid rgba(255,255,255,0.2)',
                      position: 'relative',
                      flexShrink: 0
                    }}>
                      <Video
                        videoStream={this.state.localStream}
                        autoPlay
                        muted
                        videoStyles={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transform: 'scaleX(-1)'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: isMobile ? '4px' : '8px',
                        left: isMobile ? '4px' : '8px',
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        padding: isMobile ? '2px 6px' : '4px 8px',
                        borderRadius: '4px',
                        fontSize: isMobile ? '10px' : '12px',
                        fontWeight: '500'
                      }}>
                        You
                      </div>
                    </div>

                    {/* Non-focused remote participants (3rd, 4th...) */}
                    {this.state.remoteStreams.length > 1 && (() => {
                      const focusedId = this.state.focusedStreamId || this.state.remoteStreams[0]?.id;
                      return this.state.remoteStreams
                        .filter(s => s.id !== focusedId)
                        .map((s, i) => (
                          <div
                            key={s.id || i}
                            onClick={() => this.setState({ focusedStreamId: s.id })}
                            title={`Focus on ${s.name || 'Peer'}`}
                            style={{
                              width: pipSize.width,
                              height: pipSize.height,
                              borderRadius: isMobile ? '8px' : '12px',
                              overflow: 'hidden',
                              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                              border: isMobile ? '2px solid rgba(255,255,255,0.2)' : '3px solid rgba(255,255,255,0.2)',
                              position: 'relative',
                              flexShrink: 0,
                              cursor: 'pointer',
                              transition: 'border-color 0.15s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'}
                          >
                            <Video
                              videoStream={s.video?.stream || s.stream}
                              autoPlay
                              videoStyles={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                            {/* Hover swap hint */}
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(0,0,0,0)',
                                transition: 'background 0.15s',
                                pointerEvents: 'none'
                              }}
                            />
                            <div style={{
                              position: 'absolute',
                              bottom: isMobile ? '4px' : '8px',
                              left: isMobile ? '4px' : '8px',
                              background: 'rgba(0,0,0,0.6)',
                              color: '#fff',
                              padding: isMobile ? '2px 6px' : '4px 8px',
                              borderRadius: '4px',
                              fontSize: isMobile ? '10px' : '12px',
                              fontWeight: '500',
                              pointerEvents: 'none'
                            }}>
                              {s.name || `Peer ${i + 2}`}
                            </div>
                          </div>
                        ));
                    })()}
                  </div>
                )}
              </div>

              <div style={{
                position: 'absolute',
                top: isMobile ? '12px' : '20px',
                left: isMobile ? '12px' : '20px',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                padding: isMobile ? '6px 12px' : '8px 16px',
                borderRadius: isMobile ? '16px' : '20px',
                fontSize: isMobile ? '11px' : '13px',
                fontWeight: '500',
                zIndex: 10
              }}>
                {this.state.remoteStreams.length > 0
                  ? (() => {
                      if (this.state.remoteStreams.length > 1) {
                        const focusedId = this.state.focusedStreamId || this.state.remoteStreams[0]?.id;
                        const focused = this.state.remoteStreams.find(s => s.id === focusedId);
                        return focused?.name || this.peerName;
                      }
                      return this.state.selectedVideo?.name || this.state.remoteStreams[0]?.name || this.peerName;
                    })()
                  : this.state.status}
              </div>

              {this.state.remoteStreams.map(stream => (
                stream.audio && (
                  <audio
                    key={`audio-${stream.id}`}
                    autoPlay
                    playsInline
                    ref={audio => {
                      if (audio && audio.srcObject !== stream.audio) {
                        audio.srcObject = stream.audio
                        audio.play().catch(e => console.log('[Call] Audio autoplay blocked:', e))
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                )
              ))}
            </>
          ) : (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px',
              position: 'relative'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '40px'
              }}>
                {this.state.remoteStreams.length === 0 ? (
                  <div style={{
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '20px',
                    padding: '10px',
                    textAlign: 'center',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    maxWidth: '400px'
                  }}>
                    
                    <h2 style={{
                      color: '#fff',
                      fontSize: '18px',
                      fontWeight: '600',
                      marginBottom: '5px',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}>
                      Waiting for peer...
                    </h2>
                  
                  <div style={{
                    width: '40px',
                    height: '4px',
                    background: 'rgba(255,255,255,0.3)',
                    borderRadius: '2px',
                    margin: '0 auto',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-40px',
                      width: '40px',
                      height: '100%',
                      background: 'rgba(255,255,255,0.8)',
                      animation: 'shimmer 2s infinite'
                    }}></div>
                  </div>
                </div>
              ) : (
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '20px',
                  padding: '10px',
                  textAlign: 'center',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  marginBottom: '30px'
                }}>
                  
                  <h2 style={{
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    Connected
                  </h2>
                  <p style={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '16px',
                    margin: 0
                  }}>
                    {this.state.remoteStreams.map(stream => stream.name || `Peer ${this.state.remoteStreams.indexOf(stream) + 1}`).join(', ')}
                  </p>
                </div>
              )}
            </div>

            {this.state.remoteStream && (
                <audio
                  autoPlay
                  ref={audio => {
                    if (audio) audio.srcObject = this.state.remoteStream;
                  }}
                  style={{ display: 'none' }}
                />
              )}
              {this.state.localStream && (
                <audio
                  autoPlay
                  muted
                  ref={audio => {
                    if (audio) audio.srcObject = this.state.localStream;
                  }}
                  style={{ display: 'none' }}
                />
              )}

            </div>
          )}

          <div style={{
            position: 'absolute',
            bottom: controlsBottom,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: controlsGap,
            alignItems: 'center',
            zIndex: 10
          }}>
            {/* Microphone button */}
            <button
              style={{
                width: buttonSize,
                height: buttonSize,
                borderRadius: '50%',
                border: 'none',
                background: this.state.isMicMuted ? '#ef4444' : 'rgba(255,255,255,0.15)',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(8px)',
                transform: this.state.buttonAnimations.mic ? 'scale(0.9)' : 'scale(1)'
              }}
              onMouseEnter={(e) => e.target.style.background = this.state.isMicMuted ? '#dc2626' : 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.target.style.background = this.state.isMicMuted ? '#ef4444' : 'rgba(255,255,255,0.15)'}
              onClick={() => {
                this.toggleMic()
                this.handleButtonClick('mic')
              }}
              title={this.state.isMicMuted ? "Unmute Microphone" : "Mute Microphone"}
            >
              <HiMicrophone style={{ opacity: this.state.isMicMuted ? 0.6 : 1, fontSize: isMobile ? '16px' : '18px' }} />
            </button>

            {/* Camera button (video calls only) */}
            {isVideoCall && (
              <button
                style={{
                  width: buttonSize,
                  height: buttonSize,
                  borderRadius: '50%',
                  border: 'none',
                  background: this.state.isCameraOff ? '#ef4444' : 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(8px)',
                  transform: this.state.buttonAnimations.camera ? 'scale(0.9)' : 'scale(1)'
                }}
                onMouseEnter={(e) => e.target.style.background = this.state.isCameraOff ? '#dc2626' : 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.target.style.background = this.state.isCameraOff ? '#ef4444' : 'rgba(255,255,255,0.15)'}
                onClick={() => {
                  this.toggleCamera()
                  this.handleButtonClick('camera')
                }}
                title={this.state.isCameraOff ? "Turn Camera On" : "Turn Camera Off"}
              >
                <HiVideoCamera style={{ opacity: this.state.isCameraOff ? 0.6 : 1, fontSize: isMobile ? '16px' : '18px' }} />
              </button>
            )}

            {/* Screen share button */}
            <button
              style={{
                width: buttonSize,
                height: buttonSize,
                borderRadius: '50%',
                border: 'none',
                background: this.state.isScreenSharing ? '#3b82f6' : 'rgba(255,255,255,0.15)',
                color: '#fff',
                cursor: this.state.screenShareSupported ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(8px)',
                transform: this.state.buttonAnimations.screen ? 'scale(0.9)' : 'scale(1)',
                opacity: this.state.screenShareSupported ? 1 : 0.4
              }}
              onMouseEnter={(e) => e.target.style.background = this.state.isScreenSharing ? '#2563eb' : 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.target.style.background = this.state.isScreenSharing ? '#3b82f6' : 'rgba(255,255,255,0.15)'}
              onClick={() => {
                this.toggleScreenShare()
                this.handleButtonClick('screen')
              }}
              title={
                this.state.screenShareSupported
                  ? (this.state.isScreenSharing ? 'Stop Sharing' : 'Share Screen')
                  : 'Screen share not supported on this device/browser'
              }
              disabled={!this.state.screenShareSupported}
            >
              <HiDesktopComputer style={{ fontSize: isMobile ? '16px' : '18px' }} />
            </button>

            {/* Recording button */}
            {this.state.recordingSupported && (
              <button
                style={{
                  width: buttonSize,
                  height: buttonSize,
                  borderRadius: '50%',
                  border: 'none',
                  background: this.state.isRecording ? '#ef4444' : 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(8px)',
                  transform: this.state.buttonAnimations.record ? 'scale(0.9)' : 'scale(1)'
                }}
                onMouseEnter={(e) => e.target.style.background = this.state.isRecording ? '#dc2626' : 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.target.style.background = this.state.isRecording ? '#ef4444' : 'rgba(255,255,255,0.15)'}
                onClick={() => {
                  if (this.state.isRecording) {
                    this.stopRecording()
                  } else {
                    this.startRecording()
                  }
                  this.handleButtonClick('record')
                }}
                title={this.state.isRecording ? "Stop Recording" : "Start Recording"}
              >
                {this.state.isRecording ? (
                  <BsStopCircle style={{ fontSize: isMobile ? '16px' : '18px' }} />
                ) : (
                  <BsRecordCircle style={{ fontSize: isMobile ? '16px' : '18px' }} />
                )}
              </button>
            )}

            {/* Hangup button */}
            <button
              style={{
                width: hangupButtonSize,
                height: hangupButtonSize,
                borderRadius: '50%',
                border: 'none',
                background: '#ef4444',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 16px rgba(239, 68, 68, 0.3)',
                transform: this.state.buttonAnimations.hangup ? 'scale(0.9)' : 'scale(1)'
              }}
              onMouseEnter={(e) => e.target.style.transform = this.state.buttonAnimations.hangup ? 'scale(0.9)' : 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = this.state.buttonAnimations.hangup ? 'scale(0.9)' : 'scale(1)'}
              onClick={() => {
                this.cleanupCall()
                this.handleButtonClick('hangup')
                if (this.onHangup) this.onHangup()
              }}
              title="End Call"
            >
              <HiPhone style={{ transform: 'rotate(135deg)', fontSize: isMobile ? '18px' : '20px' }} />
            </button>
          </div>
        </div>

        <style jsx>{`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }

          @keyframes shimmer {
            0% { left: -40px; }
            100% { left: 40px; }
          }
        `}</style>
      </div>
    )
  }
}

export default App
